// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// API types mirror the verifier's response shapes for `/lookup/{id}`
// and `/version`. Field names match the Rust Serialize derives byte-
// for-byte; if you rename a field on the verifier side you also
// rename it here.

import type { OwnershipProof } from './sign-via-user-app.js';

export interface Strikes {
  short_usd: number;
  long_usd: number;
}

export interface Contract {
  schema_version: number;
  vault_id: string;
  multisig_descriptor: string;
  user_payout_address: string;
  platform_refund_address: string;
  principal_sats: number;
  reward_sats: number;
  term_start_block: number;
  term_end_block: number;
  refund_unlock_block: number;
  max_loss_bps: number;
  loss_formula: string;
  strikes: Strikes;
  settlement_reference_time_unix: number;
  price_source_id: string;
}

export interface PublicVaultStatus {
  /** Lifecycle state. `active` was formerly named `finalized` — the
   *  verifier renamed it because "finalized" read as terminal, when it
   *  actually means "live and accepting operations". Accept both for a
   *  forward-compat window; new bundles emit `active`. */
  state: 'pending' | 'active' | 'finalized' | 'inactive' | 'aborted';
  created_at_unix: number;
  /** Wire field was renamed from `finalized_at_unix` → `activated_at_unix`
   *  in lockstep with the state rename. The verifier still accepts the
   *  old name on input via a serde alias, but responses emit the new
   *  name. Keep `finalized_at_unix` as a fallback for now. */
  activated_at_unix?: number | null;
  finalized_at_unix?: number | null;
  aborted_at_unix: number | null;
  settlement_record_count: number;
  latest_settlement_observed_at_unix: number | null;
}

export interface SignedReceipt {
  receipt: {
    schema_version: number;
    contract_hash_hex: string;
    vault_id: string;
    verifier_multisig_pubkey_hex: string;
    created_at_unix: number;
  };
  signature_base64: string;
}

export interface PublicLookupResponse {
  vault_id: string;
  contract: Contract;
  contract_hash_hex: string;
  /** V14: BIP-44 account index mirrored from the existence response. */
  user_derivation_index: number;
  verifier_multisig_pubkey_hex: string;
  verifier_identity_pubkey_hex: string;
  signed_receipt: SignedReceipt;
  /** The vault's on-chain deposit address, re-derived by the verifier
   *  from the contract descriptor's wsh script on every response. */
  deposit_address: string;
  /** Network `deposit_address` is encoded for: 'bitcoin', 'signet',
   *  or 'regtest' (signet and testnet share an encoding; the verifier
   *  reports the tb1 family as 'signet'). The mempool.space link is
   *  gated on 'bitcoin'. */
  bitcoin_network: string;
  status: PublicVaultStatus;
}

/** V10: existence-only response for `GET /lookup/{id}`. The field
 *  `exists` is always `true` when the request succeeds — a false /
 *  missing vault collapses into a 404 with an identical body.
 *
 *  V14: also carries `user_derivation_index` so the V13 recovery
 *  flow can sign at the correct BIP-44 slot without scanning. */
export interface PublicLookupExistsResponse {
  vault_id: string;
  exists: true;
  user_derivation_index: number;
}

export interface VersionResponse {
  name: string;
  version: string;
  commit: string;
  identity_pubkey_hex: string;
}

export type LookupExistsResult =
  | { kind: 'exists'; value: PublicLookupExistsResponse }
  | { kind: 'not-found' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

export type LookupFullResult =
  | { kind: 'ok'; value: PublicLookupResponse }
  | { kind: 'unauthorized' }
  | { kind: 'not-found' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

// ── V05: ownership-proof challenge / prove ─────────────────────────

export interface ChallengeResponse {
  nonce_hex: string;
  expires_at_unix: number;
  canonical_message: string;
}

export interface ProveResponse {
  session_token: string;
  expires_at_unix: number;
}

export type ProveResult =
  | { kind: 'ok'; value: ProveResponse }
  | { kind: 'invalid-signature' }
  | { kind: 'expired-or-used-nonce'; reason: string }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

// ── COB-245: owner contract acknowledgement ────────────────────────

export interface AckResponse {
  vault_id: string;
  acked: boolean;
  /** The verifier's stored contract hash that was acknowledged. */
  contract_hash_hex: string;
  acked_at_unix: number;
}

export type AckResult =
  | { kind: 'ok'; value: AckResponse }
  | { kind: 'unauthorized' }
  /** The stored contract changed since the owner reviewed it (409) — the
   *  echoed hash no longer matches. The owner must refresh and re-confirm
   *  the new terms. */
  | { kind: 'conflict'; message: string }
  | { kind: 'not-found' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

// ── HW.7 / HW.12: on-device contract-ack ceremony ──────────────────

/** Response body for `POST /lookup/{id}/ack-challenge` (HW.7). Mirrors the
 *  verifier's `AckChallengeResponse` byte-for-byte. The `canonical_message`
 *  is the server-authoritative string the device BSM-signs verbatim — it
 *  binds the verifier's OWN stored `contract_hash_hex` and re-derived
 *  `network`, so the human confirms the real hash on the device screen. */
export interface AckChallengeResponse {
  nonce_hex: string;
  expires_at_unix: number;
  canonical_message: string;
  contract_hash_hex: string;
  network: string;
}

/** Result of `POST /lookup/{id}/ack-v2` (HW.7). Unlike the session-gated `/ack`,
 *  this ceremony is NOT session-authorized — the authority is the fresh
 *  on-device signature (closes review A3). A 401 is opaque (failed verify OR
 *  stale/used nonce), exactly like `/prove-v2`; a 400 is a malformed
 *  proof/nonce (a device/client defect). There is no `unauthorized`
 *  (session-expired) kind here — nothing to re-prove. */
export type AckV2Result =
  | { kind: 'ok'; value: AckResponse }
  | { kind: 'invalid-signature' }
  | { kind: 'bad-proof'; reason: string }
  | { kind: 'not-found' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

// ── V07: gated settlement-record download ──────────────────────────

export interface SignedSettlementRecord {
  record: {
    schema_version: number;
    vault_id: string;
    contract_hash_hex: string;
    price_source_id: string;
    settlement_reference_time_unix: number;
    settlement_price_cents: number;
    observed_at_unix: number;
  };
  signature_base64: string;
}

export interface SettlementRecordResponse {
  signed_record: SignedSettlementRecord;
  verifier_identity_pubkey_hex: string;
}

export type SettlementRecordResult =
  | { kind: 'ok'; value: SettlementRecordResponse }
  | { kind: 'unauthorized' }
  | { kind: 'not-yet-settled' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

export const api = {
  /** V10: existence check. Public, rate-limited. Returns `exists` on
   *  a 200, `not-found` on 404 (which is the identical body for both
   *  unknown and malformed IDs — the UI renders a single "unknown
   *  vault" state so the discrimination never reaches the user). */
  async lookupExists(vaultId: string): Promise<LookupExistsResult> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}`);
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 404) return { kind: 'not-found' };
    if (response.status === 429) return { kind: 'rate-limited' };
    if (!response.ok) {
      return { kind: 'error', message: `HTTP ${response.status}` };
    }
    const value = (await response.json()) as PublicLookupExistsResponse;
    return { kind: 'exists', value };
  },

  /** V10: session-gated full lookup. Requires a bearer token minted
   *  by `/prove`. A 401 here means the token failed for any reason
   *  (missing / malformed / bad sig / expired / wrong vault) — the
   *  server does not discriminate, and neither should the caller. */
  async lookupVaultFull(
    vaultId: string,
    sessionToken: string,
  ): Promise<LookupFullResult> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}/full`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 401) return { kind: 'unauthorized' };
    if (response.status === 404) return { kind: 'not-found' };
    if (response.status === 429) return { kind: 'rate-limited' };
    if (!response.ok) {
      return { kind: 'error', message: `HTTP ${response.status}` };
    }
    const value = (await response.json()) as PublicLookupResponse;
    return { kind: 'ok', value };
  },

  async version(): Promise<VersionResponse | null> {
    try {
      const response = await fetch('/version');
      if (!response.ok) return null;
      return (await response.json()) as VersionResponse;
    } catch {
      return null;
    }
  },

  async issueChallenge(vaultId: string): Promise<ChallengeResponse | null> {
    const response = await fetch(
      `/lookup/${encodeURIComponent(vaultId)}/challenge`,
      { method: 'POST' },
    );
    if (!response.ok) return null;
    return (await response.json()) as ChallengeResponse;
  },

  async prove(
    vaultId: string,
    nonceHex: string,
    signatureDerHex: string,
  ): Promise<ProveResult> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}/prove`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          nonce_hex: nonceHex,
          signature_der_hex: signatureDerHex,
        }),
      });
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 429) return { kind: 'rate-limited' };
    if (response.status === 200) {
      return { kind: 'ok', value: (await response.json()) as ProveResponse };
    }
    if (response.status === 400) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      const reason = body.error ?? 'bad_request';
      if (reason === 'invalid_signature') return { kind: 'invalid-signature' };
      return { kind: 'expired-or-used-nonce', reason };
    }
    return { kind: 'error', message: `HTTP ${response.status}` };
  },

  /** HW.11 (COB-331): the WS-H dual-format sibling of `prove`. Posts a proof
   *  object — `bsm-bip137-v1` from a Ledger, or `der-sha256-v1` — to the
   *  verifier's `/lookup/{id}/prove-v2` (HW.7). The proof is forwarded
   *  verbatim; the server parses and verifies it against the vault's user
   *  multisig pubkey. Unlike `/prove` (which distinguishes invalid-signature
   *  from a stale nonce via a 400 body), `/prove-v2` returns an opaque **401**
   *  for both a failed verify and a stale/used nonce (verify-then-consume, so
   *  a failed verify never burns the nonce — a retry with the right device
   *  works); we surface that uniformly as `invalid-signature`. */
  async proveV2(
    vaultId: string,
    nonceHex: string,
    proof: OwnershipProof,
  ): Promise<ProveResult> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}/prove-v2`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nonce_hex: nonceHex, proof }),
      });
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 429) return { kind: 'rate-limited' };
    if (response.status === 200) {
      return { kind: 'ok', value: (await response.json()) as ProveResponse };
    }
    // 401: opaque failed-verify-or-stale-nonce (spec §7.6). 400: a malformed
    // proof or a bad nonce shape — a client/device defect, not a stale nonce.
    if (response.status === 401) return { kind: 'invalid-signature' };
    if (response.status === 400) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      return { kind: 'expired-or-used-nonce', reason: body.error ?? 'bad_request' };
    }
    return { kind: 'error', message: `HTTP ${response.status}` };
  },

  /** COB-245: record the owner's confirmation of the registered contract.
   *  Requires the ownership session token. Echoes the `contract_hash_hex`
   *  the owner reviewed; the verifier binds its own stored hash and 409s if
   *  the two differ (contract replaced since review). Idempotent on the same
   *  stored hash. This ack is the precondition user-app checks before it
   *  reveals the deposit address, and the verifier checks before activation. */
  async acknowledgeContract(
    vaultId: string,
    sessionToken: string,
    contractHashHex: string,
  ): Promise<AckResult> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}/ack`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ contract_hash_hex: contractHashHex }),
      });
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 401) return { kind: 'unauthorized' };
    if (response.status === 404) return { kind: 'not-found' };
    if (response.status === 409) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      return { kind: 'conflict', message: body.error ?? 'contract changed since review' };
    }
    if (response.status === 429) return { kind: 'rate-limited' };
    if (!response.ok) {
      return { kind: 'error', message: `HTTP ${response.status}` };
    }
    const value = (await response.json()) as AckResponse;
    return { kind: 'ok', value };
  },

  /** HW.7 / HW.12: mint a one-shot contract-ack ceremony nonce for the native
   *  hardware path. Not session-gated (the ceremony's authority is the fresh
   *  on-device signature). Returns the server-assembled canonical message the
   *  device signs, plus the verifier's own stored hash + re-derived network for
   *  the ceremony UI to display. `null` on any non-200 (rate-limited /
   *  not-found / transport error) — the caller surfaces a retry prompt. */
  async ackChallenge(vaultId: string): Promise<AckChallengeResponse | null> {
    let response: Response;
    try {
      response = await fetch(
        `/lookup/${encodeURIComponent(vaultId)}/ack-challenge`,
        { method: 'POST' },
      );
    } catch {
      return null;
    }
    if (!response.ok) return null;
    return (await response.json()) as AckChallengeResponse;
  },

  /** HW.7 / HW.12: redeem an on-device contract-ack proof. Posts the WS-H proof
   *  object (a Ledger `bsm-bip137-v1`) over the ceremony message to
   *  `/lookup/{id}/ack-v2`. Not session-gated. 401 is opaque (failed verify or
   *  stale/used nonce → `invalid-signature`, retryable with the right device);
   *  400 is a malformed proof/nonce (`bad-proof`); verify-then-consume means a
   *  failed verify never burns the nonce. */
  async ackV2(
    vaultId: string,
    nonceHex: string,
    proof: OwnershipProof,
  ): Promise<AckV2Result> {
    let response: Response;
    try {
      response = await fetch(`/lookup/${encodeURIComponent(vaultId)}/ack-v2`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nonce_hex: nonceHex, proof }),
      });
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 429) return { kind: 'rate-limited' };
    if (response.status === 200) {
      return { kind: 'ok', value: (await response.json()) as AckResponse };
    }
    if (response.status === 401) return { kind: 'invalid-signature' };
    if (response.status === 404) return { kind: 'not-found' };
    if (response.status === 400) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      return { kind: 'bad-proof', reason: body.error ?? 'bad_request' };
    }
    return { kind: 'error', message: `HTTP ${response.status}` };
  },

  async downloadSettlementRecord(
    vaultId: string,
    sessionToken: string,
  ): Promise<SettlementRecordResult> {
    let response: Response;
    try {
      response = await fetch(
        `/lookup/${encodeURIComponent(vaultId)}/settlement-record`,
        { headers: { Authorization: `Bearer ${sessionToken}` } },
      );
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
    if (response.status === 401) return { kind: 'unauthorized' };
    if (response.status === 404) return { kind: 'not-yet-settled' };
    if (response.status === 429) return { kind: 'rate-limited' };
    if (!response.ok) {
      return { kind: 'error', message: `HTTP ${response.status}` };
    }
    const value = (await response.json()) as SettlementRecordResponse;
    return { kind: 'ok', value };
  },
};

// ── UUID helper ─────────────────────────────────────────────────────

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLikelyVaultId(input: string): boolean {
  return UUID_REGEX.test(input.trim());
}
